import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicCollageApi } from '../../api/client';
import type { Collage } from '../../types';
import { resolveTheme } from '../../utils/collageThemes';

export function PublicCollagePage() {
  const { token } = useParams<{ token: string }>();
  const [collage, setCollage] = useState<Collage | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicCollageApi.get(token).then(setCollage).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center px-4">
        <p className="text-slate-500 text-sm">This collage isn't available anymore.</p>
      </div>
    );
  }

  if (!collage) {
    return (
      <div className="min-h-screen bg-[#F8F9FF] flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const theme = resolveTheme(collage.themeId);
  const sortedItems = [...collage.items].sort((a, b) => a.position - b.position);

  return (
    <div className="min-h-screen bg-[#F8F9FF]">
      <header className="bg-white border-b border-indigo-50 sticky top-0 z-40">
        <div className="px-4 lg:px-8 h-[62px] flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-[34px] h-[34px] rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
              </svg>
            </div>
            <span className="text-[1.05rem] font-bold tracking-tight text-slate-900">EventsCarousel</span>
          </Link>
          <Link to="/register" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
            Join EventsCarousel
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 lg:px-8 lg:py-10">
        <div className="mb-6 text-center">
          <span className="text-2xl">🖼️</span>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">{collage.title || 'Untitled Collage'}</h1>
          <p className="text-sm text-slate-500 mt-1">{theme.name} &middot; {collage.type === 'grid' ? 'Grid' : 'Storyboard'}</p>
        </div>

        {collage.type === 'grid' ? (
          collage.renderedImageUrl ? (
            <img src={collage.renderedImageUrl} alt={collage.title ?? 'Collage'} className="w-full rounded-xl shadow-lg" />
          ) : (
            <p className="text-center text-slate-400 text-sm">This collage isn't ready to view yet.</p>
          )
        ) : (
          <div className="flex flex-col gap-4">
            {sortedItems.map(item => (
              <div key={item.id} className="bg-white rounded-xl border border-indigo-50 shadow-sm overflow-hidden">
                <img
                  src={item.eventPhoto.filePath} alt="" loading="lazy" className="w-full aspect-[16/10] object-cover"
                  style={{ filter: theme.previewFilterCss, objectPosition: `${item.focalX}% ${item.focalY}%` }}
                />
                <div className="p-3">
                  <p className="text-[11px] font-bold text-indigo-600">
                    {item.eventPhoto.takenAt ? new Date(item.eventPhoto.takenAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Undated'}
                  </p>
                  {item.caption && <p className="text-sm text-slate-700 mt-0.5">{item.caption}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
